import { ImageSourceSizes, PlusIcon, Typography } from '@ag1/ui';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import useAddAccessibility from 'shared/hooks/addAccessibility';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { StudioHotspotImageV2 } from 'shared/studio/StudioHotspotImage';
import { ValueReviewCards } from 'types/studio/studio';
import { ExpandedBlock } from './ExpandedBlock';
import styles from './ReviewCard.module.scss';

type Props = ValueReviewCards['cards'][number] & {
  sendNavigationEvent: () => void;
  hotspotImageSizes: ImageSourceSizes;
  visibleOnPageLoad: boolean;
};

export const ReviewCard = ({
  hotspotImage,
  headline,
  description,
  expandedData,
  sendNavigationEvent,
  hotspotImageSizes,
  visibleOnPageLoad
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useAddAccessibility(contentRef, true);
  const [open, setOpen] = useState(false);

  if (!hotspotImage || !expandedData) {
    return null;
  }

  const handleOpen = () => {
    setOpen(!open);
    if (!open) {
      sendNavigationEvent();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleOpen();
    }
  };

  return (
    <div
      className={styles.root}
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      data-gtm-skip-global
      onKeyDown={handleKeyDown}>
      <StudioHotspotImageV2
        className={styles.image}
        image={hotspotImage}
        sizes={hotspotImageSizes}
        eagerLoading={visibleOnPageLoad}
      />
      <div ref={contentRef} className={styles.basicText}>
        {headline && (
          <Typography variant="headlineSans" size="3" color="black-new" noPadding>
            {headline}
          </Typography>
        )}
        {description && <CustomPortableText value={description} className={styles.description} />}
        <div className={clsx(styles.iconContainer, open && styles.iconRotate)}>
          <span className={styles.icon}>
            <PlusIcon size={24} strokeWidth={1} />
          </span>
        </div>
      </div>
      <ExpandedBlock open={open} {...expandedData} />
    </div>
  );
};
